import {
  HolderOutlined,
  ReloadOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  PointerSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import {
  Button,
  Checkbox,
  Empty,
  Popover,
  Table,
  TableColumnType,
  TablePaginationConfig,
  TableProps,
} from 'antd';
import React, { useEffect, useMemo, useState } from 'react';

import { PageListProps } from './PageList.interface';
import * as S from './PageList.style';
import { usePageListColumnOrder } from './usePageListColumnOrder';
import { usePageListColumnVisibility } from './usePageListColumnVisibility';

const getColumnKey = (column: TableColumnType<any>) => {
  return column.key === undefined ? undefined : String(column.key);
};

interface DragIndexState {
  active: UniqueIdentifier | null;
  over: UniqueIdentifier | null;
  direction?: 'left' | 'right';
}

const defaultDragIndexState: DragIndexState = {
  active: null,
  over: null,
};

const DragIndexContext = React.createContext<DragIndexState>(
  defaultDragIndexState
);

const dragActiveStyle = (
  dragState: DragIndexState,
  columnKey: string | undefined
): React.CSSProperties => {
  if (!columnKey) return {};

  const { active, direction, over } = dragState;

  if (active === columnKey) {
    return {
      backgroundColor: '#f0f0f0',
      opacity: 0.5,
    };
  }

  if (over === columnKey && active !== over) {
    return direction === 'right'
      ? { borderInlineEnd: '1px dashed #8c8c8c' }
      : { borderInlineStart: '1px dashed #8c8c8c' };
  }

  return {};
};

interface HeaderCellProps extends React.HTMLAttributes<HTMLTableCellElement> {
  'data-column-key'?: string;
  'data-column-fixed'?: boolean;
}

interface BodyCellProps extends React.HTMLAttributes<HTMLTableCellElement> {
  'data-column-key'?: string;
}

const SortableHeaderCell = ({
  children,
  'data-column-key': columnKey,
  'data-column-fixed': isFixedColumn = false,
  ...props
}: HeaderCellProps) => {
  const dragState = React.useContext(DragIndexContext);
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } =
    useSortable({
      id: columnKey || '',
      disabled: !columnKey || isFixedColumn,
    });

  const style: React.CSSProperties = {
    ...props.style,
    ...(isDragging
      ? { position: 'relative', userSelect: 'none', zIndex: 9999 }
      : {}),
    ...dragActiveStyle(dragState, columnKey),
  };

  return (
    <th {...props} ref={setNodeRef} style={style}>
      <S.DragColumnHeaderContent>
        {columnKey && !isFixedColumn && (
          <S.DragColumnHandle
            ref={setActivatorNodeRef}
            type='button'
            aria-label='Mover coluna'
            onClick={event => event.stopPropagation()}
            onMouseDown={event => event.stopPropagation()}
            {...attributes}
            {...listeners}
          >
            <HolderOutlined />
          </S.DragColumnHandle>
        )}
        <S.DragColumnHeaderTitle>{children}</S.DragColumnHeaderTitle>
      </S.DragColumnHeaderContent>
    </th>
  );
};

const TableBodyCell = ({
  'data-column-key': columnKey,
  ...props
}: BodyCellProps) => {
  const dragState = React.useContext(DragIndexContext);

  return (
    <td
      {...props}
      style={{
        ...props.style,
        ...dragActiveStyle(dragState, columnKey),
      }}
    />
  );
};

export const PageListTemplate: React.FC<PageListProps<any>> = ({
  columns,
  data,
  filterComponent,
  loading,
  amoutLabel,
  onClickRow,
  onChange,
  onChangeSort,
  pageSize = 20,
  currentPage = 1,
  hidePagination,
  onChangePagination,
  columnVisibilityConfig,
  columnVisibilityState,
  columnOrderState,
  enableDragColumn = false,
  dragColumnStorageKey,
  fixedColumnKeys: configuredFixedColumnKeys = [],
  stickyHeader = false,
}) => {
  const [dragIndex, setDragIndex] = useState<DragIndexState>(
    defaultDragIndexState
  );
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    })
  );

  const internalColumnOrderState = usePageListColumnOrder(columns, {
    enabled: enableDragColumn,
    persist: Boolean(dragColumnStorageKey),
    storageKey: dragColumnStorageKey,
    fixedColumnKeys: configuredFixedColumnKeys,
  });
  const {
    orderedColumns,
    columnOrder,
    fixedColumnKeys,
    setColumnOrder,
    handleResetColumnOrder,
    hasCustomColumnOrder,
  } = columnOrderState || internalColumnOrderState;

  const internalColumnVisibilityState = usePageListColumnVisibility(
    orderedColumns,
    columnVisibilityConfig
  );
  const {
    isColumnVisibilityEnabled,
    availableColumns,
    visibleColumns,
    hiddenColumnKeys,
    alwaysVisibleColumnKeys,
    handleToggleColumnVisibility,
    handleResetColumnVisibility,
    hasHiddenColumns,
  } = columnVisibilityState || internalColumnVisibilityState;
  const tableColumns = useMemo(() => {
    if (!enableDragColumn) return visibleColumns;

    return visibleColumns.map(column => {
      const columnKey = getColumnKey(column);

      if (!columnKey) return column;

      return {
        ...column,
        onHeaderCell: (columnData: any) => {
          const headerCellProps = column.onHeaderCell?.(columnData);

          return {
            ...headerCellProps,
            style: {
              ...headerCellProps?.style,
              height: 'auto',
              paddingBlock: 8,
            },
            'data-column-key': columnKey,
            'data-column-fixed': fixedColumnKeys.includes(columnKey),
            'aria-label':
              typeof column.title === 'string' ? column.title : undefined,
          };
        },
        onCell: (record: any, rowIndex?: number) => ({
          ...column.onCell?.(record, rowIndex),
          'data-column-key': columnKey,
        }),
      };
    });
  }, [enableDragColumn, fixedColumnKeys, visibleColumns]);
  const [isColumnVisibilityPopoverOpen, setIsColumnVisibilityPopoverOpen] =
    useState(false);
  const [pagination, setPagination] = React.useState<TablePaginationConfig>({
    total: 0,
    current: currentPage,
    position: ['bottomCenter'],
    responsive: true,
    hideOnSinglePage: true,
    showSizeChanger: true,
    showQuickJumper: true,
    size: 'small',
    pageSize,
  });

  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      current: currentPage || prev.current,
      total: data?.count || 0,
      pageSize: pageSize || prev.pageSize,
    }));
  }, [currentPage, data, pageSize]);

  const handleChange: TableProps<any>['onChange'] = (
    paramsPagination,
    filters,
    sorter,
    extra
  ) => {
    if (sorter && !Array.isArray(sorter)) {
      onChangeSort?.({
        field: sorter.field as string,
        order: sorter.order || null,
      });
    }
    if (
      pagination.current === paramsPagination.current &&
      pagination.pageSize === paramsPagination.pageSize
    )
      return;

    setPagination(prev => ({
      ...prev,
      ...paramsPagination,
    }));
    onChange?.(
      paramsPagination.pageSize || 1,
      ((paramsPagination?.current || 1) - 1) * (paramsPagination.pageSize || 0),
      paramsPagination.current || 1
    );

    if (extra.action === 'paginate') {
      onChangePagination?.(
        pagination.current || currentPage,
        pagination.pageSize || pageSize
      );
    }
  };

  const columnVisibilityContent = (
    <S.ColumnVisibilityMenu
      onClick={event => event.stopPropagation()}
      onMouseDown={event => event.stopPropagation()}
    >
      {availableColumns.map(column => {
        const columnKey = column.key;
        const isAlwaysVisible = alwaysVisibleColumnKeys.includes(columnKey);

        return (
          <S.ColumnVisibilityOption key={columnKey}>
            <Checkbox
              checked={isAlwaysVisible || !hiddenColumnKeys.includes(columnKey)}
              disabled={isAlwaysVisible}
              onChange={() => handleToggleColumnVisibility(columnKey)}
            >
              {column.label}
            </Checkbox>
          </S.ColumnVisibilityOption>
        );
      })}
      <S.ColumnResetActions>
        {isColumnVisibilityEnabled && (
          <Button
            type='text'
            aria-label='Restaurar colunas visíveis'
            icon={<ReloadOutlined />}
            disabled={!hasHiddenColumns}
            onClick={handleResetColumnVisibility}
          >
            Restaurar colunas visíveis
          </Button>
        )}
        {enableDragColumn && (
          <Button
            type='text'
            aria-label='Restaurar ordem das colunas'
            icon={<ReloadOutlined />}
            disabled={!hasCustomColumnOrder}
            onClick={handleResetColumnOrder}
          >
            Restaurar ordem das colunas
          </Button>
        )}
      </S.ColumnResetActions>
    </S.ColumnVisibilityMenu>
  );

  const shouldShowColumnConfiguration =
    isColumnVisibilityEnabled || enableDragColumn;

  const columnVisibilityButton = shouldShowColumnConfiguration ? (
    <Popover
      content={columnVisibilityContent}
      title='Colunas'
      trigger='click'
      placement='bottomRight'
      open={isColumnVisibilityPopoverOpen}
      onOpenChange={setIsColumnVisibilityPopoverOpen}
      destroyOnHidden
    >
      <Button aria-label='Colunas' icon={<SettingOutlined />}>
        Colunas
      </Button>
    </Popover>
  ) : null;

  const getFixedBoundary = (order: string[], columnIndex: number) => {
    return order
      .slice(0, columnIndex)
      .filter(columnKey => fixedColumnKeys.includes(columnKey)).length;
  };

  const canMoveColumn = (
    order: string[],
    activeId: UniqueIdentifier,
    overId: UniqueIdentifier
  ) => {
    const activeKey = String(activeId);
    const overKey = String(overId);
    const activeIndex = order.indexOf(activeKey);
    const overIndex = order.indexOf(overKey);

    if (
      activeIndex === -1 ||
      overIndex === -1 ||
      fixedColumnKeys.includes(activeKey) ||
      fixedColumnKeys.includes(overKey)
    ) {
      return false;
    }

    return (
      getFixedBoundary(order, activeIndex) ===
      getFixedBoundary(order, overIndex)
    );
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      setDragIndex(defaultDragIndexState);
      return;
    }

    setColumnOrder(prev => {
      const activeIndex = prev.findIndex(columnKey => columnKey === active.id);
      const overIndex = prev.findIndex(columnKey => columnKey === over.id);

      if (!canMoveColumn(prev, active.id, over.id)) return prev;

      return arrayMove(prev, activeIndex, overIndex);
    });
    setDragIndex(defaultDragIndexState);
  };

  const onDragOver = ({ active, over }: DragOverEvent) => {
    if (!over || !canMoveColumn(columnOrder, active.id, over.id)) {
      setDragIndex({ active: active.id, over: null });
      return;
    }

    const activeIndex = tableColumns.findIndex(
      column => getColumnKey(column) === active.id
    );
    const overIndex = tableColumns.findIndex(
      column => getColumnKey(column) === over?.id
    );

    setDragIndex({
      active: active.id,
      over: over?.id ?? null,
      direction: overIndex > activeIndex ? 'right' : 'left',
    });
  };

  const activeDragColumn = tableColumns.find(
    column => getColumnKey(column) === dragIndex.active
  );

  const onDragCancel = () => {
    setDragIndex(defaultDragIndexState);
  };

  const table = (
    <Table
      rowKey={'id'}
      bordered
      loading={loading}
      pagination={hidePagination ? false : pagination}
      columns={tableColumns}
      dataSource={data?.rows || []}
      onChange={handleChange}
      sticky={stickyHeader}
      components={
        enableDragColumn
          ? {
              header: {
                cell: SortableHeaderCell,
              },
              body: {
                cell: TableBodyCell,
              },
            }
          : undefined
      }
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={'Nenhum registro encontrado'}
          />
        ),
      }}
      onRow={record => ({
        style: !onClickRow ? undefined : { cursor: 'pointer' },
        onClick: () => {
          if (onClickRow) {
            onClickRow(record);
          }
        },
      })}
    />
  );

  const sortableTable = enableDragColumn ? (
    <DndContext
      sensors={sensors}
      modifiers={[restrictToHorizontalAxis]}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDragCancel={onDragCancel}
    >
      <SortableContext
        items={columnOrder}
        strategy={horizontalListSortingStrategy}
      >
        <DragIndexContext.Provider value={dragIndex}>
          {table}
        </DragIndexContext.Provider>
      </SortableContext>
      <DragOverlay>
        {activeDragColumn ? (
          <S.DragColumnOverlay>
            {activeDragColumn.title as React.ReactNode}
          </S.DragColumnOverlay>
        ) : null}
      </DragOverlay>
    </DndContext>
  ) : (
    table
  );

  return (
    <S.Container>
      <S.FilterHeaderContainer>{filterComponent}</S.FilterHeaderContainer>
      {shouldShowColumnConfiguration && (
        <S.TableHeaderActions>{columnVisibilityButton}</S.TableHeaderActions>
      )}
      {amoutLabel && data?.rows.length && !hidePagination && (
        <S.TableTotalAmountContainer>{amoutLabel}</S.TableTotalAmountContainer>
      )}

      {sortableTable}
    </S.Container>
  );
};

export type { PageListProps };
